import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import toast from 'react-hot-toast';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress, 
    Tooltip, 
    IconButton,
} from '@mui/material';
import { 
    Download,
    AddCircleOutline, 
    Edit,
    Delete,
    DeleteForever,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import api from 'state/axios';
import { LAYOUT_HEIGHT } from 'state/constants';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import { exportToExcel } from 'state/excel';
import { logClientAction } from 'state/logger';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import DataGridComponent from 'components/DataGridComponent';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import TravelAllowanceEntriesList from './TravelAllowanceEntriesList';


const TravelAllowanceEntries = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const user = useSelector((state) => state.user);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const permissionMap = useMemo(
        () => buildPermissionMap(user?.permissions),
        [user?.permissions]
    );
    const canCreate = hasRight(permissionMap, 'TravelAllowanceEntries', 'create', false);
    const canEdit   = hasRight(permissionMap, 'TravelAllowanceEntries', 'update', false);
    const canDelete = hasRight(permissionMap, 'TravelAllowanceEntries', 'delete', false);
    const canExport = hasRight(permissionMap, 'TravelAllowanceEntries', 'export', false);

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState(null);
    const [idToDelete, setIdToDelete] = useState(null);

    const [entries, setEntries] = useState([]);


    useEffect(() => {
        const fetchEntries = async () => {
            try {
                if (!startDate || !endDate) return;
                setError(null);
                setLoading(true);
                const res = await api.post('/travelallowanceentries', { startDate, endDate });
                setEntries(res.data);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };
        fetchEntries();
    }, [startDate, endDate]);

    const columns = useMemo(() => [
        {
            field: 'no',
            headerName: 'No',
            filterable: false,
            sortable: false,
            width: 60,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
        },
        {
            field: 'destination',
            headerName: 'Destination',
            width: 100,
        },
        {
            field: 'travelZone',
            headerName: 'TravelZone',
            width: 90,
        },
        {
            field: 'isInternational',
            headerName: 'Int\'l',
            width: 60,
            renderCell: (params) => (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">{params.value ? 'Yes' : 'No'}</Typography>
                </Box>
            ),
        },
        {
            field: 'startDate',
            headerName: 'StartDate',
            align: 'center',
            width: 130,
            valueFormatter: (value) => value ? formatInTimeZone(value, 'UTC', 'HH:mm dd/MM/yyyy') : '',
        },
        {
            field: 'endDate',
            headerName: 'EndDate',
            align: 'center',
            width: 130,
            valueFormatter: (value) => value ? formatInTimeZone(value, 'UTC', 'HH:mm dd/MM/yyyy') : '',
        },
        {
            field: 'travelHours',
            headerName: 'TravelHours',
            width: 90,
            renderCell: (params) => (
                <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Typography variant="body2">{params.value ?? ''}</Typography>
                </Box>
            ),
        },
        {
            field: 'perdiem',
            headerName: 'Perdiem',
            width: 80,
            renderCell: (params) => (
                <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Typography variant="body2">{params.value ?? ''}</Typography>
                </Box>
            ),
        },
        {
            field: 'crewMembers',
            headerName: 'CrewMembers',
            width: 360,
            renderCell: (params) => {
                const sorted = [...(params.row.crewMembers || [])].sort((a, b) =>
                    String(a.crewCode).localeCompare(String(b.crewCode))
                );
                return (
                    <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
                        {sorted.map((member, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: '30px' }}>
                                    {member.crewCode}
                                </Typography>
                                <Typography variant="body2">{member.crewName}</Typography>
                                <Typography variant="body2">({member.rank} | {member.base})</Typography>
                            </Box>
                        ))}
                    </Box>
                );
            },
        },
        {
            field: 'crewCount',
            headerName: 'CrewCount',
            align: 'right',
            width: 90,
            renderCell: (params) => (
                <span>{Array.isArray(params.row.crewMembers) ? params.row.crewMembers.length : 0}</span>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 90,
        },
        {
            field: 'description',
            headerName: 'Description',
            width: 250,
        },
    ], []);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

    const actionColumn = [
        {
            field: 'action',
            headerName: 'Action',
            filterable: false,
            sortable: false,
            width: 90,
            renderCell: (params) => (
                <Box display="flex" justifyContent="center" alignItems="center" gap={1} sx={{ width: '100%', height: '100%' }}>
                    <Tooltip title="Edit">
                        <IconButton
                            aria-label="Edit"
                            sx={{ color: theme.palette.primary.dark, transition: 'color 0.3s ease', '&:hover': { color: theme.palette.green.deep } }}
                            onClick={() => {
                                if (!canEdit) return;
                                navigate(`/travelallowanceentries/${params.row._id}`);
                            }}
                        >
                            <Edit />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            aria-label="Delete"
                            sx={{ color: theme.palette.primary.dark, transition: 'color 0.3s ease', '&:hover': { color: theme.palette.green.deep } }}
                            onClick={() => {
                                if (!canDelete) return;
                                handleDeleteClick(params.row._id);
                            }}
                        >
                            <DeleteForever />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    const handleDeleteClick = (id) => {
        setIdToDelete(id);
        setDeleteType('single');
        setConfirmDialogOpen(true);
    };

    const handleBulkDelete = () => {
        const selectedIds = getSelectedIds(rowSelectionModel, entries);
        if (selectedIds.length === 0) {
            toast.error('No rows selected for deletion!');
            return;
        }
        setDeleteType('bulk');
        setConfirmDialogOpen(true);
    };

    const executeDelete = useCallback(async () => {
        try {
            if (!canDelete) return;

            if (deleteType === 'single') {
                if (!idToDelete) return;
                await api.patch('/travelallowanceentries/soft-delete', { ids: [idToDelete] });
                setEntries(entries.filter((item) => item._id !== idToDelete));
                toast.success('Entry deleted successfully.');
            } else if (deleteType === 'bulk') {
                const selectedIds = getSelectedIds(rowSelectionModel, entries);
                if (selectedIds.length === 0) {
                    toast.error('No rows selected for deletion!');
                    return;
                }
                await api.patch('/travelallowanceentries/soft-delete', { ids: selectedIds });
                setEntries(entries.filter((item) => !selectedIds.includes(item._id)));
                toast.success(`${selectedIds.length} record(s) deleted successfully.`);
            }
        } catch (err) {
            toast.error(err.message || 'Failed to delete!');
        }
    }, [canDelete, deleteType, idToDelete, rowSelectionModel, entries]);

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Travel Allowance Entries"
                    subtitle={`${loading ? 'Loading...' : 'List of Entries'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        {canExport && (
                            <Tooltip title="Download Excel file" arrow>
                                <span>
                                    <StyledButton
                                        onClick={() => {
                                            const omitFields = ['deletedAt', 'updatedAt', 'createdAt', 'createdBy', 'updatedBy', '__v'];
                                            exportToExcel(entries, 'TravelAllowanceEntries', omitFields);
                                            logClientAction('EXPORT_REPORT', { fileName: 'TravelAllowanceEntries', format: 'Excel', startDate, endDate });
                                        }}
                                    >
                                        <Download />
                                    </StyledButton>
                                </span>
                            </Tooltip>
                        )}

                        {canDelete && (
                            <Tooltip title="Bulk soft delete" arrow>
                                <span>
                                    <StyledButton
                                        disabled={!isAnyRowSelected(rowSelectionModel, entries)}
                                        onClick={handleBulkDelete}
                                    >
                                        <Delete />
                                    </StyledButton>
                                </span>
                            </Tooltip>
                        )}

                        {canCreate && (
                            <Tooltip title="Create" arrow>
                                <span>
                                    <StyledButton onClick={() => navigate('/travelallowanceentries/new')}>
                                        <AddCircleOutline />
                                    </StyledButton>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gap={2}
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >
                    <Box 
                        ref={scrollRef} 
                        height={LAYOUT_HEIGHT} 
                        sx={{ gridColumn: 'span 12', overflowY: 'auto' }}
                    >
                        {isMobile ? (
                            <TravelAllowanceEntriesList
                                rows={entries || []}
                                canEdit={canEdit}
                                canDelete={canDelete}
                                onEdit={(id) => navigate(`/travelallowanceentries/${id}`)}
                                onDelete={handleDeleteClick}
                            />
                        ) : (
                            <DataGridComponent
                                rows={entries || []}
                                columns={columns.concat(actionColumn)}
                                columnVisibilityModel={columnVisibilityModel}
                                setColumnVisibilityModel={setColumnVisibilityModel}
                                loading={loading}
                                props={{
                                    checkboxSelection: true,
                                    disableRowSelectionOnClick: true,
                                    initialState: {
                                        pagination: { paginationModel: { pageSize: 100 } },
                                    },
                                    onRowSelectionModelChange: (newModel) => setRowSelectionModel(newModel),
                                    rowSelectionModel,
                                    getRowClassName: (params) =>
                                        params.row.isActive === false ? 'isActive-false' : '',
                                }}
                                sx={{
                                    '& .MuiDataGrid-row.isActive-false': {
                                        backgroundColor: `${theme.palette.primary.light} !important`,
                                    },
                                }}
                            />
                        )}

                        <ScrollToTop scrollRef={scrollRef} />
                        
                        <DeleteConfirm
                            open={confirmDialogOpen}
                            onClose={() => setConfirmDialogOpen(false)}
                            onConfirm={executeDelete}
                            deleteType={deleteType}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default TravelAllowanceEntries;
